import { useEffect, useMemo, useState } from 'react'
import { Trash2 } from 'lucide-react'
import { useProfile, candidateSections, type Experience } from '@/hooks/use-profile'
import { SkillChipField } from '@/components/skills/skill-chip-field'
import {
  compareMonthYear,
  isCompleteMonthYear,
  monthYearDraftKey,
  monthYearToIso,
  parseIsoToMonthYear,
  type MonthYearDraft,
} from '@/lib/month-year'
import { appendSkillName, sortedSkillNamesKey } from '@/utils/profile-skill-suggestions'
import { Button } from '@/components/ui/button'
import { MonthYearRangeFields } from '@/components/ui/month-year-field'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import {
  ArtifactCanvasBody,
  ArtifactCanvasFooterBar,
  ArtifactCanvasInlineError,
  ArtifactCanvasSaveActions,
} from '@/components/artifact/artifact-canvas-layout'

interface ExperienceCanvasProps {
  experience: Experience | null
  onClose: () => void
}

interface ExperienceDraft {
  title: string
  company: string
  start?: MonthYearDraft
  end?: MonthYearDraft
  currentRole: boolean
  description: string
  skills: string[]
}

function buildDraft(experience: Experience | null): ExperienceDraft {
  return {
    title: experience?.title ?? '',
    company: experience?.company ?? '',
    start: parseIsoToMonthYear(experience?.start_date),
    end: parseIsoToMonthYear(experience?.end_date),
    currentRole: experience ? !experience.end_date : true,
    description: experience?.description ?? '',
    skills: [...(experience?.skills ?? [])],
  }
}

export function ExperienceCanvas({ experience, onClose }: ExperienceCanvasProps) {
  const {
    profile,
    addExperienceAsync,
    updateExperienceAsync,
    deleteExperienceAsync,
    addExperienceSkillsAsync,
    removeExperienceSkillsAsync,
  } = useProfile()
  const candidate = candidateSections(profile)

  const isEditMode = experience !== null

  const liveExperience = useMemo<Experience | null>(() => {
    if (!isEditMode || !experience) return null
    const match = (candidate.experiences ?? []).find((e) => e.id === experience.id)
    return match ?? experience
  }, [isEditMode, experience, candidate.experiences])

  const initialDraft = useMemo(() => buildDraft(liveExperience), [liveExperience])
  const [draft, setDraft] = useState<ExperienceDraft>(initialDraft)
  const [validationError, setValidationError] = useState<string | null>(null)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  useEffect(() => {
    setDraft(buildDraft(liveExperience))
    setValidationError(null)
    setSubmitError(null)
    setConfirmingDelete(false)
  }, [liveExperience])

  const fieldsDirty =
    draft.title !== initialDraft.title ||
    draft.company !== initialDraft.company ||
    draft.description !== initialDraft.description ||
    monthYearDraftKey(draft.start) !== monthYearDraftKey(initialDraft.start) ||
    monthYearDraftKey(draft.end) !== monthYearDraftKey(initialDraft.end) ||
    draft.currentRole !== initialDraft.currentRole

  const skillsDirty = sortedSkillNamesKey(draft.skills) !== sortedSkillNamesKey(initialDraft.skills)
  const isDirty = isEditMode ? fieldsDirty || skillsDirty : true

  const attemptAddRelatedSkill = (rawName: string) => {
    const result = appendSkillName(draft.skills, rawName)
    if (!result.ok) {
      setValidationError(result.error)
      return
    }
    setDraft((current) => ({ ...current, skills: result.skills }))
    setValidationError(null)
  }

  const removeSkill = (skill: string) => {
    setDraft((current) => ({
      ...current,
      skills: current.skills.filter((value) => value !== skill),
    }))
    setValidationError(null)
  }

  const handleSave = async () => {
    setValidationError(null)
    setSubmitError(null)

    if (!draft.title.trim()) {
      setValidationError('Title is required.')
      return
    }
    if (!draft.company.trim()) {
      setValidationError('Company is required.')
      return
    }
    if (!isCompleteMonthYear(draft.start)) {
      setValidationError('Start month and year are required.')
      return
    }
    if (!draft.currentRole && !isCompleteMonthYear(draft.end)) {
      setValidationError(
        'End month and year are required, or mark that you currently work in this role.',
      )
      return
    }
    if (
      !draft.currentRole &&
      isCompleteMonthYear(draft.end) &&
      compareMonthYear(draft.end, draft.start) < 0
    ) {
      setValidationError('End date cannot be before start date.')
      return
    }

    const startIso = monthYearToIso(draft.start)
    const endIso = draft.currentRole || !isCompleteMonthYear(draft.end)
      ? undefined
      : monthYearToIso(draft.end)

    setIsSaving(true)
    try {
      if (!isEditMode) {
        const created = await addExperienceAsync({
          title: draft.title.trim(),
          company: draft.company.trim(),
          start_date: startIso,
          end_date: endIso,
          description: draft.description.trim(),
        })

        if (draft.skills.length > 0) {
          await addExperienceSkillsAsync({
            experienceId: created.id,
            skillNames: draft.skills,
          })
        }
      } else if (liveExperience) {
        if (fieldsDirty) {
          await updateExperienceAsync({
            id: liveExperience.id,
            updates: {
              title: draft.title.trim(),
              company: draft.company.trim(),
              start_date: startIso,
              end_date: endIso ?? null,
              description: draft.description.trim(),
            },
          })
        }

        if (skillsDirty) {
          const initialLower = new Set(
            (liveExperience.skills ?? []).map((s) => s.trim().toLowerCase())
          )
          const draftLower = new Set(draft.skills.map((s) => s.trim().toLowerCase()))

          const toAdd = draft.skills.filter(
            (s) => !initialLower.has(s.trim().toLowerCase())
          )
          const toRemove = (liveExperience.skills ?? []).filter(
            (s) => !draftLower.has(s.trim().toLowerCase())
          )

          if (toAdd.length > 0) {
            await addExperienceSkillsAsync({
              experienceId: liveExperience.id,
              skillNames: toAdd,
            })
          }

          if (toRemove.length > 0) {
            await removeExperienceSkillsAsync({
              experienceId: liveExperience.id,
              skillNames: toRemove,
            })
          }
        }
      }

      onClose()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to save experience.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!isEditMode || !liveExperience) return

    if (!confirmingDelete) {
      setConfirmingDelete(true)
      return
    }

    setSubmitError(null)
    setIsDeleting(true)
    try {
      await deleteExperienceAsync(liveExperience.id)
      onClose()
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to delete experience.')
      setIsDeleting(false)
      setConfirmingDelete(false)
    }
  }

  const isBusy = isSaving || isDeleting

  return (
    <ArtifactCanvasBody className="flex h-full min-h-0 flex-col space-y-0 p-0">
      <form
        className="min-h-0 flex-1 space-y-6 overflow-y-auto p-6"
        onSubmit={(event) => event.preventDefault()}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="artifact-experience-title">Title *</Label>
            <Input
              id="artifact-experience-title"
              value={draft.title}
              onChange={(event) => {
                setDraft((current) => ({ ...current, title: event.target.value }))
                setValidationError(null)
              }}
              placeholder="e.g., Software Engineer"
              disabled={isBusy}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="artifact-experience-company">Company *</Label>
            <Input
              id="artifact-experience-company"
              value={draft.company}
              onChange={(event) => {
                setDraft((current) => ({ ...current, company: event.target.value }))
                setValidationError(null)
              }}
              placeholder="e.g., Acme Pty Ltd"
              disabled={isBusy}
            />
          </div>
        </div>

        <MonthYearRangeFields
          idPrefix="artifact-experience"
          ongoingLabel="I currently work in this role"
          ongoingChecked={draft.currentRole}
          onOngoingChange={(checked) => {
            setDraft((current) => ({
              ...current,
              currentRole: checked,
              end: checked ? undefined : current.end,
            }))
            setValidationError(null)
          }}
          start={draft.start}
          end={draft.end}
          onStartChange={(start) => {
            setDraft((current) => ({ ...current, start }))
            setValidationError(null)
          }}
          onEndChange={(end) => {
            setDraft((current) => ({ ...current, end }))
            setValidationError(null)
          }}
          startRequired
          disabled={isBusy}
        />

        <div className="space-y-2">
          <Label htmlFor="artifact-experience-description">Description</Label>
          <Textarea
            id="artifact-experience-description"
            rows={4}
            value={draft.description}
            onChange={(event) => {
              setDraft((current) => ({ ...current, description: event.target.value }))
            }}
            placeholder="What did you do in this role?"
            disabled={isBusy}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="artifact-experience-skills-input">Skills</Label>
          <SkillChipField
            id="artifact-experience-skills-input"
            skills={draft.skills}
            profileSkills={candidate.skills}
            disabled={isBusy}
            onAdd={attemptAddRelatedSkill}
            onRemove={removeSkill}
            placeholder="Add a skill demonstrated in this role…"
            helperText="Pick from your profile skills or create a new one — it will be added to your profile when you save."
          />
        </div>
      </form>

      <ArtifactCanvasInlineError>{validationError || submitError}</ArtifactCanvasInlineError>

      <ArtifactCanvasFooterBar
        leading={
          isEditMode ? (
            <Button
              type="button"
              variant={confirmingDelete ? 'destructive' : 'ghost'}
              onClick={handleDelete}
              disabled={isBusy}
              className={confirmingDelete ? '' : 'text-destructive hover:text-destructive'}
            >
              <Trash2 className="h-4 w-4 mr-1" />
              {isDeleting ? 'Deleting...' : confirmingDelete ? 'Confirm delete' : 'Delete'}
            </Button>
          ) : undefined
        }
        actions={
          <ArtifactCanvasSaveActions
            onClose={onClose}
            onSave={handleSave}
            isDirty={isDirty}
            isSaving={isSaving}
            saveLabel={isEditMode ? 'Save' : 'Add experience'}
            savingLabel="Saving..."
            closeDisabled={isBusy}
            saveDisabled={!isDirty || isBusy}
          />
        }
      />
    </ArtifactCanvasBody>
  )
}
